/**
 * Le contrat de la revue KYC, côté navigateur (`B-09`, `B-10`).
 *
 * **Ce fichier ne décide de rien.** Il transporte, et il déclare ce que le serveur
 * rend — les types suivent `KycReviewMessages`, sans rien y ajouter.
 *
 * **Il n'y a pas de champ de motif en texte libre, et il n'y en aura pas.** Une
 * décision négative porte un `reasonCode` choisi dans les listes que le serveur
 * rend avec le dossier. C'est le contrat lui-même qui rend le texte libre
 * inexprimable : l'écran ne peut pas offrir un champ qui n'existe nulle part.
 * Le client le reçoit **traduit dans sa langue** et doit pouvoir corriger seul ;
 * un texte libre ne se traduit pas, ne se compte pas, et le renvoie au support.
 *
 * **Le motif d'audit et le motif de décision sont deux choses.** `reason` est la
 * phrase que l'analyste écrit pour le journal d'audit — pourquoi il a ouvert ce
 * dossier. `reasonCode` est ce que le client lira. Les confondre enverrait au
 * client la note interne de l'analyste.
 */

import type { ApiBytes, ApiClient } from './api-client.js';

/** Le chemin de la file, tel que `KycReviewApi` le sert. */
export const kycQueuePath = '/api/backoffice/kyc/queue';

/** Le chemin d'un dossier. */
export function kycCasePath(caseId: string): string {
  return `/api/backoffice/kyc/cases/${caseId}`;
}

/** Le chemin qui sert les **octets** d'une capture, tel que `KycReviewApi` le sert. */
export const kycImagePath = '/api/backoffice/kyc/images';

/** Le chemin de la route unique des actions du socle. */
export function backOfficeActionPath(action: string): string {
  return `/api/backoffice/actions/${action}`;
}

/** Une ligne de la file. */
export interface KycQueueRow {
  readonly caseId: string;
  readonly customerId: string;
  /**
   * La référence **publique du compte** du client — `3HXZLPC256`.
   *
   * Ce n'est **pas** la référence du dossier. La maquette écrit
   * « KYC-4021-88F3 » ; `compliance.kyc_case` n'a pas de colonne de référence
   * lisible, seulement une clé primaire `UUID`, et rien ne sert donc celle-là.
   * Celle-ci est réelle, servie et à taille humaine.
   *
   * Nulle quand la fiche du client n'a pas été retrouvée. L'écran le déclare.
   */
  readonly customerPublicRef: string | null;
  /**
   * Le prénom et le nom du client, **séparés**.
   *
   * Le serveur ne les assemble pas : « Prénom Nom » est une mise en forme, et
   * elle appartient à l'interface, qui connaît la langue de l'agent. Les deux
   * peuvent être nuls — les colonnes le sont, et un compte ouvert au seul numéro
   * de téléphone n'a pas encore de nom.
   */
  readonly customerFirstName: string | null;
  readonly customerLastName: string | null;
  readonly targetLevel: number;
  readonly channel: string;
  readonly reviewStatus: string;
  readonly priority: number;
  readonly queuedAt: string;
  /** Ancienneté en secondes, mesurée par le **serveur**. */
  readonly ageSeconds: number;
  /** Vrai au-delà du seuil d'ancienneté paramétré. */
  readonly ageing: boolean;
  readonly assignedTo: string | null;
  readonly alertCount: number;
  /** Le guichet qui a enrôlé, ou `null` pour un dossier ordinaire. */
  readonly outletCode: string | null;
  readonly operatorRef: string | null;
  readonly customerValidated: boolean;
}

/** La file, et les deux durées qu'il faut pour la lire. */
export interface KycQueue {
  readonly rows: readonly KycQueueRow[];
  readonly targetSeconds: number;
  readonly ageingAlertSeconds: number;
}

/** Une capture, et l'URL signée de courte durée qui permet de la regarder. */
export interface KycCapture {
  readonly side: string;
  readonly signedUrl: string;
  readonly expiresAt: string;
  readonly capturedAt: string;
  readonly source: string;
  readonly deviceFingerprint: string;
  readonly perceptualHash: string;
}

/** Une alerte de rapprochement. */
export interface KycAlert {
  readonly typeCode: string;
  readonly evidence: string;
  readonly raisedAt: string;
}

/** Un motif proposable : son code, et le texte que le **client** recevra. */
export interface KycReason {
  readonly code: string;
  readonly text: string;
}

/** La pièce déclarée. */
export interface KycDocument {
  readonly type: string;
  readonly number: string;
  readonly issuingCountry: string;
  readonly issuedOn: string | null;
  readonly expiresOn: string | null;
}

/** L'écran des 90 secondes, en un seul aller-retour. */
export interface KycCaseView {
  readonly row: KycQueueRow;
  readonly gestureInstruction: string | null;
  readonly document: KycDocument | null;
  readonly captures: readonly KycCapture[];
  readonly alerts: readonly KycAlert[];
  readonly approvalReasons: readonly KycReason[];
  readonly rejectionReasons: readonly KycReason[];
  readonly informationReasons: readonly KycReason[];
  readonly targetSeconds: number;
}

/** Les trois décisions, et le code d'action que chacune emprunte. */
export const kycDecisionActions = {
  approve: 'KYC_APPROVE',
  complement: 'KYC_INFO_REQUEST',
  reject: 'KYC_REJECT',
} as const;

/** L'identifiant d'une décision, tel que la coquille de file le manipule. */
export type KycDecisionId = keyof typeof kycDecisionActions;

/** Lit la file de revue. Le motif est obligatoire : la lecture est journalisée. */
export async function fetchKycQueue(client: ApiClient, reason: string): Promise<KycQueue> {
  return client.request<KycQueue>(`${kycQueuePath}?reason=${encodeURIComponent(reason)}`);
}

/** Lit un dossier, avec ses URL signées et ses motifs proposables. */
export async function fetchKycCase(
  client: ApiClient,
  caseId: string,
  reason: string,
): Promise<KycCaseView> {
  return client.request<KycCaseView>(
    `${kycCasePath(caseId)}?reason=${encodeURIComponent(reason)}`,
  );
}

/**
 * Lit les **octets** d'une capture, à partir de l'URL signée que le dossier a rendue.
 *
 * `signedUrl` n'est pas une adresse : c'est un **jeton opaque** — la référence
 * de coffre et sa péremption, scellées. Posé tel quel dans un `src`, il se
 * résout relativement à l'écran et ne mène nulle part ; c'est exactement ce que
 * l'écran faisait, et pourquoi l'analyste ne voyait que deux icônes cassées.
 *
 * L'appel passe par `requestBytes`, donc par le **jeton de session** : la
 * signature dit *quelle* image, le jeton dit *qui regarde*, et le serveur a
 * besoin des deux — la seconde pour inscrire au journal d'audit qui a vu la
 * pièce (`EX-BO-004`, `EX-DAT-007`).
 *
 * Le motif d'audit accompagne la lecture, comme pour la file et le dossier.
 */
export async function fetchKycImage(
  client: ApiClient,
  signedUrl: string,
  reason: string,
): Promise<ApiBytes> {
  return client.requestBytes(
    `${kycImagePath}?url=${encodeURIComponent(signedUrl)}` +
      `&reason=${encodeURIComponent(reason)}`,
  );
}

/** S'affecte un dossier. Refusé si un autre analyste le tient encore. */
export async function assignKycCase(
  client: ApiClient,
  caseId: string,
  reason: string,
): Promise<void> {
  await client.request(backOfficeActionPath('KYC_CASE_ASSIGN'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      entityRef: caseId,
      reason,
      payload: { caseId },
    }),
  });
}

/**
 * Rend une décision.
 *
 * `reasonCode` est **obligatoire**, y compris pour une approbation : la base
 * exige un motif normalisé sur toute décision humaine, et l'approbation en a
 * un — « votre dossier est conforme ». C'est ce qui interdit qu'une décision
 * s'écrive avec une phrase tapée à la main.
 */
export async function decideKycCase(
  client: ApiClient,
  options: {
    readonly caseId: string;
    readonly decision: KycDecisionId;
    readonly reasonCode: string;
    readonly auditReason: string;
  },
): Promise<void> {
  await client.request(backOfficeActionPath(kycDecisionActions[options.decision]), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      entityRef: options.caseId,
      reason: options.auditReason,
      payload: { caseId: options.caseId, reasonCode: options.reasonCode },
    }),
  });
}
